import React from "react";
import {
    Package,
    CheckCircle,
    XCircle,
    Box,
    PackageX,
    AlertTriangle,
    CalendarPlus,
} from "lucide-react";
import { computeProductMetrics } from "./productMetricsUtils";

const METRICS = [
    {
        key: "all",
        label: "Total Products",
        icon: Package,
        bg: "from-purple-300/20 to-blue-300/20",
        iconBg: "from-purple-500 to-blue-500",
        valueKey: "total",
    },
    {
        key: "active",
        label: "Active",
        icon: CheckCircle,
        bg: "from-emerald-300/20 to-green-300/20",
        iconBg: "from-emerald-500 to-green-600",
        valueKey: "active",
    },
    {
        key: "inactive",
        label: "Inactive",
        icon: XCircle,
        bg: "from-red-300/20 to-pink-300/20",
        iconBg: "from-red-500 to-pink-500",
        valueKey: "inactive",
    },
    {
        key: "inStock",
        label: "In Stock",
        icon: Box,
        bg: "from-blue-300/20 to-indigo-300/20",
        iconBg: "from-blue-600 to-indigo-500",
        valueKey: "inStock",
    },
    {
        key: "outOfStock",
        label: "Out of Stock",
        icon: PackageX,
        bg: "from-yellow-200/20 to-orange-200/20",
        iconBg: "from-yellow-500 to-orange-500",
        valueKey: "outOfStock",
    },
    {
        key: "lowStock",
        label: "Low Stock",
        icon: AlertTriangle,
        bg: "from-teal-200/20 to-cyan-200/20",
        iconBg: "from-teal-500 to-cyan-500",
        valueKey: "lowStock",
        warnWhenPositive: true,
        warnLabel: "Restock needed",
    },
    {
        key: "addedThisMonth",
        label: "Added This Month",
        icon: CalendarPlus,
        bg: "from-violet-300/20 to-fuchsia-300/20",
        iconBg: "from-violet-500 to-fuchsia-500",
        valueKey: "addedThisMonth",
    },
];

const ProductMetricsCards = ({ products, activeFilter, onFilterChange }) => {
    const counts = computeProductMetrics(products);

    const handleClick = (key) => {
        onFilterChange(key === "all" ? "all" : key);
    };

    return (
        <div
            className="products-metrics-row"
            role="toolbar"
            aria-label="Product catalog metrics"
        >
            {METRICS.map((item) => {
                const Icon = item.icon;
                const value = counts[item.valueKey] ?? 0;
                const isActive = activeFilter === item.key || (item.key === "all" && activeFilter === "all");
                const showWarn = item.warnWhenPositive && value > 0;

                return (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => handleClick(item.key)}
                        aria-pressed={isActive}
                        className={`products-metrics-card flex items-center gap-3 p-3 rounded-xl bg-gradient-to-br ${item.bg} backdrop-blur-md border shadow-sm text-left transition-all ${
                            isActive
                                ? "border-blue-400/70 ring-2 ring-blue-400/35"
                                : "border-white/40 hover:border-blue-300/50"
                        }`}
                    >
                        <div
                            className={`w-10 h-10 shrink-0 rounded-full bg-gradient-to-br ${item.iconBg} text-white flex items-center justify-center shadow-md`}
                        >
                            <Icon className="w-5 h-5" aria-hidden />
                        </div>

                        <div className="min-w-0">
                            <p
                                className={`text-lg md:text-xl font-bold leading-tight ${
                                    showWarn ? "text-red-600" : "text-gray-900"
                                }`}
                            >
                                {value.toLocaleString("en-IN")}
                            </p>
                            <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
                                {item.label}
                                {showWarn && (
                                    <span className="products-metrics-card__warn">{item.warnLabel}</span>
                                )}
                            </p>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default ProductMetricsCards;
